/**
 * Avatar upload control: the current avatar + "Upload photo" (file picker)
 * and "Take photo" (webcam capture) actions. Posts the chosen image as
 * multipart to `endpoint` (self: /api/profile/avatar; admin:
 * /api/admin/users/:id/avatar), then busts the shared avatar cache so the
 * new photo appears everywhere (header, roster, list) without a reload.
 *
 * Mirrors portal-api's `Uploads::Validator` avatar policy client-side
 * (≤2 MB, jpeg/png/gif/webp) for an instant error instead of a round-trip.
 */
import { Box, Button, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";

import { ApiError, apiUploadForm } from "../lib/api.js";
import { CapturePhotoDialog } from "./CapturePhotoDialog.js";
import { UserAvatar, bustAvatar } from "./UserAvatar.js";

const MAX_BYTES = 2 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export function AvatarUploader({
  userId,
  name,
  endpoint,
  size = 96,
  onUploaded,
}: {
  userId: number;
  name: string;
  /** Multipart POST target. The image rides as the `avatar` form field. */
  endpoint: string;
  size?: number;
  onUploaded?: () => void;
}): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneAt, setDoneAt] = useState<number | null>(null);

  async function handleFile(file: File): Promise<void> {
    setError(null);
    setDoneAt(null);
    if (!ALLOWED.includes(file.type)) {
      setError("Choose a JPG, PNG, GIF or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image must be under 2 MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("avatar", file, file.name || "avatar.jpg");
      await apiUploadForm(endpoint, form);
      bustAvatar(userId);
      setDoneAt(Date.now());
      onUploaded?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <UserAvatar userId={userId} name={name} size={size} />
      <Box sx={{ display: "grid", gap: 0.75 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? "Uploading…" : "Upload photo"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => setCapturing(true)}
          >
            Take photo
          </Button>
        </Stack>
        {error ? (
          <Typography sx={{ fontSize: 12, color: "error.main" }}>{error}</Typography>
        ) : doneAt !== null ? (
          <Typography sx={{ fontSize: 12, color: "success.main" }}>Photo updated</Typography>
        ) : (
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
            JPG, PNG, GIF or WebP — up to 2 MB.
          </Typography>
        )}
      </Box>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ""; // allow re-selecting the same file
          if (f) void handleFile(f);
        }}
      />
      {capturing && (
        <CapturePhotoDialog
          onClose={() => setCapturing(false)}
          onCapture={(f) => {
            setCapturing(false);
            void handleFile(f);
          }}
        />
      )}
    </Stack>
  );
}
